import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React from "react";
import Section from "./Section";
import Placeholder from "./Placeholder";
import InfosTooltip from "./InfosTooltip";
import styled from "@emotion/styled";
import { useIntl } from "react-intl";
import AreaTypes from "types/AreaTypes";
import QueueAreaData from "types/QueueAreaData";
import { QueueManagementOptions } from "types/QueueManagementOptions";
// @ts-ignore
import PedestrianStatisticsUtils from "services/pedestrianStatistics/utils";

type QueueAreasDetails = {
  timestamp: number;
  data: Array<QueueAreaData>;
  error?: Response;
};

type Props = {
  initialized: boolean;
  selectedAreas: Array<AreaTypes>;
  pedestrianStatistics: {
    config: {
      queue_management: Array<QueueManagementOptions>;
    };
    queue: QueueAreasDetails;
  };
};

const TitleWithTooltipContainer = styled.div`
  display: flex;
  align-items: flex-end;
`;

const QueueComparisonTable = ({
  initialized = false,
  selectedAreas,
  pedestrianStatistics,
}: Props) => {
  const data = pedestrianStatistics.queue;
  const settings = pedestrianStatistics.config?.queue_management;
  const { formatMessage } = useIntl();
  if (!initialized) {
    return (
      <Section
        title={formatMessage({ id: "components.queueComparisonTable.title" })}
      >
        <Placeholder delay={500} ready={false} />
      </Section>
    );
  }

  return (
    <Section
      title={formatMessage({ id: "components.queueComparisonTable.title" })}
    >
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ fontWeight: "bold" }}>
                {formatMessage({ id: "components.queueComparisonTable.zone" })}
              </TableCell>
              <TableCell align="left" style={{ fontWeight: "bold" }}>
                {formatMessage({
                  id: "components.queueComparisonTable.nbPeople",
                })}
              </TableCell>
              <TableCell align="left" style={{ fontWeight: "bold" }}>
                {formatMessage({
                  id: "components.queueComparisonTable.waitingTime",
                })}
              </TableCell>
              <TableCell align="left" style={{ fontWeight: "bold" }}>
                <TitleWithTooltipContainer>
                  <span>
                    {formatMessage({
                      id: "components.queueComparisonTable.historically",
                    })}
                  </span>
                  <InfosTooltip
                    infos={formatMessage({
                      id: "components.queueComparisonTable.threeMonthsTooltip",
                    })}
                  />
                </TitleWithTooltipContainer>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!data.error &&
              data.data &&
              data.data.map((row) => {
                const condition =
                  PedestrianStatisticsUtils.getBackofficeSettingsConditionWithValue(
                    settings,
                    row.area,
                    row.waiting_time_in_seconds / 60
                  );
                const lastThreeMonthsAverageCondition =
                  PedestrianStatisticsUtils.getBackofficeSettingsConditionWithValue(
                    settings,
                    row.area,
                    row.average_time_in_seconds / 60
                  );
                const conditionColor =
                  PedestrianStatisticsUtils.getLabelBackgroundColorByAreaCondition(
                    pedestrianStatistics,
                    settings.find(
                      (queueManagementConfig: QueueManagementOptions) =>
                        queueManagementConfig.area_id === row.area
                    ),
                    null,
                    { area_id: row.area }
                  );

                return (
                  <TableRow key={row.area}>
                    <TableCell>
                      {selectedAreas.find(
                        (selectedArea) => selectedArea.id === row.area
                      )?.long_name ?? ""}
                    </TableCell>
                    <TableCell align="left">{row.value}</TableCell>
                    <TableCell align="left" style={{ color: conditionColor }}>
                      {condition
                        ? `${condition.symbol} ${condition.value} min`
                        : "-"}
                    </TableCell>
                    <TableCell align="left">
                      {lastThreeMonthsAverageCondition
                        ? `${lastThreeMonthsAverageCondition.symbol} ${lastThreeMonthsAverageCondition.value} min`
                        : "-"}
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </Section>
  );
};

export default QueueComparisonTable;
